import { GetStaticPaths, GetStaticProps } from 'next';

import {
  PrismicPageQueryDocument,
  PrismicPagePathsDocument,
} from '@prismic/gqlTypes/prismicHooks';
import {
  PrismicPageQuery,
  PrismicPageQueryVariables,
  PrismicPagePathsQuery,
  PrismicPagePathsQueryVariables,
} from '@prismic/gqlTypes/prismicOperations';

import {
  incrementalStaticRegenerationRevalidate,
  staticPathsFallback,
} from '@temp/constants';

import { prismicApolloClient } from '@utils/ssg';
import { localeToPrismicQuery, prismicToLocale } from '@utils/regions';

import { generatePrismicSeo } from '@utils/seo';
import { paths } from '@temp/paths';

import { PrismicPage, PrismicPageProps } from '@temp/views/PrismicPage';

export default PrismicPage;

export const getStaticPaths: GetStaticPaths<any> = async ({ locales }) => {
  const paths = [];

  await Promise.all(
    locales.map(async (locale) => {
      const { data } = await prismicApolloClient.query<
        PrismicPagePathsQuery,
        PrismicPagePathsQueryVariables
      >({
        query: PrismicPagePathsDocument,
        variables: { ...localeToPrismicQuery(locale) },
      });

      const lpaths = data.allPages.edges.map(({ node }) => ({
        params: { slug: node._meta.uid },
        locale: prismicToLocale(node._meta.lang),
      }));

      paths.push(...lpaths);
    })
  );

  return { paths, fallback: staticPathsFallback };
};

export const getStaticProps: GetStaticProps<
  PrismicPageProps,
  PrismicPageProps['params']
> = async ({ params: { slug }, locale, defaultLocale }) => {
  const { data } = await prismicApolloClient.query<
    PrismicPageQuery,
    PrismicPageQueryVariables
  >({
    query: PrismicPageQueryDocument,
    variables: {
      uid: slug,
      ...localeToPrismicQuery(locale),
    },
  });

  if (!data?.page) {
    return {
      notFound: true,
    };
  }

  const seo = await generatePrismicSeo(
    data.page,
    paths.page,
    'website',
    locale,
    defaultLocale
  );

  return {
    revalidate: incrementalStaticRegenerationRevalidate,
    props: {
      page: data.page,
      seo,
      params: { slug },
    },
  };
};
